import React, { useEffect, useRef, useState } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { FaMapMarkerAlt } from 'react-icons/fa';

const DeliveryMap = ({ address, exactLocation, driverLocation, height = "400px", width = "100%", onDriverMarkerDragEnd, onDestinationResolved }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [destinationLocation, setDestinationLocation] = useState(null);
  const driverMarkerRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      setLoading(true);
      setError("");
      try {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (!apiKey) {
          throw new Error("Missing Google Maps API Key. Please create a .env file with VITE_GOOGLE_MAPS_API_KEY.");
        }

        setOptions({
          apiKey: apiKey,
          version: "weekly",
        });

        const { Map } = await importLibrary('maps');
        const { Marker } = await importLibrary('marker');
        const { Geocoder } = await importLibrary('geocoding');
        const { DirectionsService, DirectionsRenderer } = await importLibrary('routes');

        if (!isMounted) return;

        // Default to a central location (e.g., India or city center) if geocoding fails
        const defaultLocation = { lat: 20.5937, lng: 78.9629 }; 
        
        const newMap = new Map(mapRef.current, {
          center: defaultLocation,
          zoom: 15,
          mapTypeControl: false,
        });
        
        setMap(newMap);

        // We will create the destination marker when we geocode it successfully
        let destMarker = null;

        // Setup Directions API
        directionsServiceRef.current = new DirectionsService();
        directionsRendererRef.current = new DirectionsRenderer({
          map: newMap,
          suppressMarkers: true, // We use custom markers
          polylineOptions: {
            strokeColor: '#3b82f6',
            strokeWeight: 5,
            strokeOpacity: 0.8
          }
        });

        // Initialize driver marker if location is already provided
        if (driverLocation) {
          driverMarkerRef.current = new Marker({
            map: newMap,
            position: driverLocation,
            title: "Delivery Driver (Drag to Simulate)",
            draggable: true,
            icon: {
              url: "https://maps.google.com/mapfiles/kml/shapes/motorcycling.png",
              scaledSize: { width: 32, height: 32 }
            }
          });
          
          if (onDriverMarkerDragEnd) {
             driverMarkerRef.current.addListener('dragend', (e) => {
                 onDriverMarkerDragEnd({ lat: e.latLng.lat(), lng: e.latLng.lng() });
             });
          }
          
          newMap.setCenter(driverLocation);
        }

        // Set destination location using exact coordinates if available
        if (exactLocation) {
          newMap.setCenter(exactLocation);
          
          destMarker = new Marker({
            map: newMap,
            position: exactLocation,
          });
          setMarker(destMarker);
          
          if (isMounted) setDestinationLocation(exactLocation);
        } else if (address) {
          // Geocode the address to find actual coordinates
          const geocoder = new Geocoder();
          geocoder.geocode({ address: address }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              newMap.setCenter(location);
              
              destMarker = new Marker({
                map: newMap,
                position: location,
              });
              setMarker(destMarker);
              
              // Trigger route drawing by updating state
              if (isMounted) {
                  setDestinationLocation(location);
                  if (onDestinationResolved) {
                      onDestinationResolved({ lat: location.lat(), lng: location.lng() });
                  }
              }
            } else {
              console.warn("Geocode was not successful for the following reason: " + status);
              if (isMounted) setError("Could not find exact location on map.");
            }
          });
        }
      } catch (err) {
        console.error("Error loading Google Maps:", err);
        if (isMounted) setError(err.message || "Failed to load map.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initMap();

    return () => {
      isMounted = false;
    };
  }, [address, exactLocation]);

  // Handle live driver location updates and routing
  useEffect(() => {
    if (map && driverLocation) {
      if (!driverMarkerRef.current) {
        importLibrary('marker').then(({ Marker }) => {
          driverMarkerRef.current = new Marker({
            map: map,
            position: driverLocation,
            title: "Delivery Driver (Drag to Simulate)",
            draggable: true,
            icon: {
              url: "https://maps.google.com/mapfiles/kml/shapes/motorcycling.png",
              scaledSize: { width: 32, height: 32 }
            }
          });
          
          if (onDriverMarkerDragEnd) {
             driverMarkerRef.current.addListener('dragend', (e) => {
                 onDriverMarkerDragEnd({ lat: e.latLng.lat(), lng: e.latLng.lng() });
             });
          }
          // Only pan if we don't have a destination yet
          if (!destinationLocation) map.panTo(driverLocation);
        }).catch(err => console.error("Error loading marker for driver", err));
      } else {
        // Animate or jump to new position
        driverMarkerRef.current.setPosition(driverLocation);
      }

      // Update Route dynamically if we have both driver and destination
      if (destinationLocation && directionsServiceRef.current && directionsRendererRef.current) {
        directionsServiceRef.current.route({
          origin: driverLocation,
          destination: destinationLocation,
          travelMode: 'DRIVING'
        }, (response, status) => {
          if (status === 'OK') {
            directionsRendererRef.current.setDirections(response);
          }
        });
      }
    }
  }, [driverLocation, map, destinationLocation]);

  return (
    <div style={{ width, height, position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      {loading && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc', zIndex: 10 }}>
          <p style={{ color: '#64748b' }}>Loading Map...</p>
        </div>
      )}
      {error && !loading && (
        <div style={{ position: 'absolute', top: 10, left: 10, right: 10, backgroundColor: '#fee2e2', color: '#ef4444', padding: '10px', borderRadius: '8px', zIndex: 10, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
          <FaMapMarkerAlt />
          {error}
        </div>
      )}
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default DeliveryMap;
